/**
 * Lod — the distant soldier.
 *
 * WHY THIS EXISTS. A soldier is one SkinnedMesh with four material groups, and four
 * groups is four draw calls **in every pass**: the colour pass, the geometry prepass
 * and each shadow cascade he falls into. Measured on `char_squad`, one soldier is
 * redrawn 4.75 times per frame, so he costs 19 calls and 76 k triangles — and twelve of
 * him is 228 of the game's 400-call budget before the level has drawn anything.
 *
 * Neither of the two obvious tricks works in three.js r185:
 *   - `material.visible = false` on a shadow-only proxy is checked by BOTH
 *     `projectObject` and `WebGLShadowMap.renderObject`, so the proxy stops casting.
 *   - `object.layers` is tested in the shadow map against the *scene camera's* layers,
 *     not the light's, so a proxy on a hidden layer is skipped there too.
 * The number of draws is a property of the GEOMETRY'S GROUP COUNT, so the only real
 * lever is to hand the renderer a mesh with fewer groups. Hence: LOD.
 *
 *   LOD0  < 13 m   full mesh, 4 groups, ~16 k tris   (4 calls/pass)
 *   LOD1  13-22 m  this mesh, 2 groups, ~2.0 k tris  (2 calls/pass)
 *   LOD2  > 22 m   this mesh, 1 group,  ~2.0 k tris  (1 call/pass)
 *
 * LOD2 folds the skin into the cloth material. At 22 m a head is 12 px across and the
 * camo albedo is a slow gradient at that scale, so what survives is the vertex tint —
 * which still says "face". It is the same geometry as LOD1; only the slot assignment
 * differs, so the two share every line of code below.
 *
 * The silhouette is what matters at this range, so the LOD keeps the helmet shell, the
 * vest bulk, the boots and the carbine, and throws away every strap, pouch flap, MOLLE
 * row, finger and ear.
 *
 * owner: characters
 */
import * as THREE from 'three';
import { SLOT, lerp } from './Build.js';
import { makeWeightFn, tubeRings, resample } from './Body.js';
import { HEAD, HEAD_RINGS, skullPoint, faceTint, helmetBrimY, shellPoint, shellPhiFor } from './Head.js';
import { weaponAnchors } from './Rig.js';

const _p = new THREE.Vector3();

/** @param {boolean} mergeSkin fold the skin group into the cloth group (LOD2) */
export function buildLodInto(B, rig, look, mergeSkin) {
  const S = rig.scale;
  const I = (n) => rig.index.get(n);
  // Only two slots exist in this mesh: 0 = skin, 1 = everything else. Using any of
  // the body mesh's four would put the group count straight back to four.
  const SKIN = mergeSkin ? SLOT.CLOTH : SLOT.SKIN;
  const KIT = SLOT.CLOTH;
  const G = look.girth ?? 1;
  const skin = new THREE.Color(look.skin);
  const hairCol = new THREE.Color(look.hair ?? 0x2b2119);
  const hairMul = [hairCol.r / Math.max(0.02, skin.r), hairCol.g / Math.max(0.02, skin.g),
    hairCol.b / Math.max(0.02, skin.b)];

  const torsoW = makeWeightFn(rig, [
    [0.90 * S, { hips: 1 }], [1.09 * S, { hips: 0.25, spine01: 0.75 }],
    [1.23 * S, { spine01: 0.30, spine02: 0.70 }], [1.36 * S, { spine02: 0.10, chest: 0.90 }],
    [1.46 * S, { chest: 0.85, neck: 0.15 }],
  ]);

  /* ---- torso + vest as one silhouette ---- */
  {
    B.setSlot(KIT).setTint(look.uniform);
    const pts = resample([
      [0, 0.845, 0.010], [0, 0.975, 0.002], [0, 1.105, 0.010],
      [0, 1.235, 0.014], [0, 1.345, 0.004], [0, 1.450, -0.010],
    ].map((p) => [p[0] * S, p[1] * S, p[2] * S]), 8);
    // The collar ring stays FAT: at 15 m the tell is a long pink neck on narrow
    // shoulders, so the trapezius mass has to survive the decimation even though the
    // vest's shoulder straps do not.
    const prof = [[0, 0.150, 0.114], [0.18, 0.152, 0.116], [0.36, 0.176, 0.140],
      [0.58, 0.194, 0.152], [0.80, 0.196, 0.148], [0.93, 0.152, 0.122], [1, 0.104, 0.092]];
    B.loft(tubeRings(pts, (d, t) => {
      let i = 0;
      while (i < prof.length - 2 && prof[i + 1][0] < t) i++;
      const u = (t - prof[i][0]) / (prof[i + 1][0] - prof[i][0]);
      const g = t < 0.9 ? G : 1;
      return { rx: lerp(prof[i][1], prof[i + 1][1], u) * g * S, ry: lerp(prof[i][2], prof[i + 1][2], u) * g * S, round: 0.66 };
    }, (d) => torsoW(0.845 * S + d)), { sides: 12, capStart: true, capEnd: true });

    // the plate carrier's mass, which is most of a soldier's upper silhouette
    B.setTint(look.vest);
    const vp = resample([[0, 1.115, 0], [0, 1.250, 0], [0, 1.390, 0]].map((p) => [p[0], p[1] * S, p[2]]), 4);
    B.loft(tubeRings(vp, (d, t) => ({
      rx: lerp(0.172, 0.196, Math.sin(t * Math.PI)) * G * S,
      ry: lerp(0.140, 0.158, Math.sin(t * Math.PI)) * G * S, round: 0.58,
    }), (d) => torsoW(1.115 * S + d)), { sides: 12, capStart: true, capEnd: true });

    // Three lumps of kit. At 15 m a plate carrier is not MOLLE webbing, it is the
    // stepped front profile of two rows of magazines and a radio on the back — and a
    // vest without them is the flat slab the reviewer complained about, decimated.
    const wV = torsoW(1.20 * S);
    B.setTint(0x6e6c5e);
    B.superBox({
      centre: new THREE.Vector3(0, 1.190 * S, 0.196 * G * S),
      half: [0.100 * S, 0.062 * S, 0.030 * S], round: 0.34, segU: 8, segV: 4, w: wV,
    });
    B.superBox({
      centre: new THREE.Vector3(-0.052 * S, 1.318 * S, 0.180 * G * S),
      half: [0.058 * S, 0.042 * S, 0.024 * S], round: 0.36, segU: 7, segV: 4, w: torsoW(1.318 * S),
    });
    if (look.radio) {
      B.setTint(0x3b3d3a);
      B.superBox({
        centre: new THREE.Vector3((look.seed % 2 ? 0.09 : -0.09) * S, 1.290 * S, -0.178 * G * S),
        half: [0.044 * S, 0.066 * S, 0.026 * S], round: 0.3, segU: 7, segV: 4, w: torsoW(1.29 * S),
      });
    }
  }

  /* ---- neck + head ---- */
  {
    B.setSlot(SKIN).setTint(look.skin);
    const np = resample([[0, 1.432, -0.004], [0, 1.505, -0.008], [0, HEAD.neckTopY, -0.012]]
      .map((p) => [p[0] * S, p[1] * S, p[2] * S]), 4);
    B.loft(tubeRings(np, (d, t) => ({
      rx: lerp(0.070, 0.042, t) * S, ry: lerp(0.074, 0.047, t) * S, round: 0.86,
    }), makeWeightFn(rig, [[0, { chest: 0.6, neck: 0.4 }], [0.08 * S, { neck: 0.7, head: 0.3 }],
      [0.16 * S, { head: 1 }]])), { sides: 8, capStart: false, capEnd: false });

    const w = [[I('head'), 1]];
    const sides = 16;
    const rings = HEAD_RINGS.filter((_, i) => i % 4 === 0 || i === HEAD_RINGS.length - 1);
    const rows = [];
    for (const y of rings) {
      const row = [];
      for (let j = 0; j <= sides; j++) {
        const th = ((j % sides) / sides) * Math.PI * 2;
        skullPoint(y, th, _p);
        const t = faceTint(_p.x, _p.y, _p.z, hairMul);
        const c = [skin.r * t[0], skin.g * t[1], skin.b * t[2]];
        _p.multiplyScalar(S);
        row.push(B.pushVertex(_p, (j / sides) * 0.5 * S, y * S, w, c));
      }
      B._seams.push(row[0], row[sides]);
      rows.push(row);
    }
    for (let i = 0; i < rows.length - 1; i++) {
      for (let j = 0; j < sides; j++) B.quad(rows[i][j], rows[i][j + 1], rows[i + 1][j + 1], rows[i + 1][j]);
    }
    const top = rows[rows.length - 1];
    const cc = B.pushVertex(_p.set(0, HEAD.hairTopY * S, -0.004 * S), 0, 0, w,
      [skin.r * hairMul[0], skin.g * hairMul[1], skin.b * hairMul[2]]);
    for (let j = 0; j < sides; j++) B.tri(cc, top[j], top[j + 1]);
  }

  /* ---- face covering. Without it the recon men in char_squad are bald pink domes
   * with bare faces at 20 m — the exact defect the reviewer found on the near
   * soldiers, reproduced at range by an LOD that dropped it. ---- */
  if (look.faceCover !== 'none' || look.shemagh) {
    const w = [[I('head'), 1]];
    const top = look.shemagh ? HEAD.noseBaseY + 0.013 : HEAD.eyeY - 0.007;
    B.setSlot(KIT).setTint(look.shemagh ? 0xc4bb9e : 0x3a352e);
    const rows = [];
    const fs = 12, frows = 3;
    for (let i = 0; i <= frows; i++) {
      const y = lerp(HEAD.jawBottomY - 0.006, top, i / frows);
      const row = [];
      for (let j = 0; j <= fs; j++) {
        const th = ((j % fs) / fs) * Math.PI * 2;
        skullPoint(y, th, _p, 0.007).multiplyScalar(S);
        row.push(B.pushVertex(_p, (j / fs) * 0.4 * S, y * S, w));
      }
      B._seams.push(row[0], row[fs]);
      rows.push(row);
    }
    for (let i = 0; i < frows; i++) {
      for (let j = 0; j < fs; j++) B.quad(rows[i][j], rows[i][j + 1], rows[i + 1][j + 1], rows[i + 1][j]);
    }
    // and the hood over the cranium, for the same reason as in Gear.js
    if (!look.shemagh) {
      const hood = [];
      for (let i = 0; i <= 2; i++) {
        const y = lerp(HEAD.browY + 0.006, HEAD.crownY, i / 2);
        const row = [];
        for (let j = 0; j <= fs; j++) {
          const th = ((j % fs) / fs) * Math.PI * 2;
          skullPoint(y, th, _p, 0.005).multiplyScalar(S);
          row.push(B.pushVertex(_p, (j / fs) * 0.4 * S, y * S, w));
        }
        B._seams.push(row[0], row[fs]);
        hood.push(row);
      }
      for (let i = 0; i < 2; i++) {
        for (let j = 0; j < fs; j++) B.quad(hood[i][j], hood[i][j + 1], hood[i + 1][j + 1], hood[i + 1][j]);
      }
      const top = hood[2];
      const cc = B.pushVertex(_p.set(0, (HEAD.crownY + 0.006) * S, -0.004 * S), 0, 0, w);
      for (let j = 0; j < fs; j++) B.tri(cc, top[j], top[j + 1]);
    }
  }

  /* ---- head covering: whatever this loadout wears, as one dome ---- */
  {
    const w = [[I('head'), 1]];
    const hs = 14, hrows = 4;
    if (look.helmet) {
      B.setSlot(KIT).setTint(look.helmetCover ? 0x8f8b78 : 0x54544a);
      const grid = [];
      for (let i = 0; i <= hrows; i++) {
        const row = [];
        for (let j = 0; j <= hs; j++) {
          const th = ((j % hs) / hs) * Math.PI * 2;
          shellPoint(shellPhiFor(helmetBrimY(th)) * Math.pow(1 - i / hrows, 1.15), th, _p).multiplyScalar(S);
          row.push(B.pushVertex(_p, (j / hs) * 0.5 * S, i * 0.03 * S, w));
        }
        B._seams.push(row[0], row[hs]);
        grid.push(row);
      }
      for (let i = 0; i < hrows; i++) {
        for (let j = 0; j < hs; j++) B.quad(grid[i][j], grid[i][j + 1], grid[i + 1][j + 1], grid[i + 1][j]);
      }
      const top = grid[hrows];
      const cc = B.pushVertex(_p.set(0, (HEAD.helmetTopY + 0.001) * S, 0), 0, 0, w);
      for (let j = 0; j < hs; j++) B.tri(cc, top[j], top[j + 1]);
      // the NVG mount is a real silhouette cue even at 20 m
      B.superBox({
        centre: shellPoint(shellPhiFor(1.7700), 0, _p, 0.010).clone().multiplyScalar(S),
        half: [0.026 * S, 0.030 * S, 0.022 * S], round: 0.3, segU: 6, segV: 4, w,
      });
    } else if (look.boonie) {
      B.setSlot(KIT).setTint(look.uniform);
      const bp = resample([[0, 1.760, -0.004], [0, 1.714, -0.004], [0, 1.696, -0.004]]
        .map((p) => [p[0], p[1] * S, p[2] * S]), 4);
      B.loft(tubeRings(bp, (d, t) => ({ rx: lerp(0.078, 0.172, t * t) * S, ry: lerp(0.084, 0.180, t * t) * S, round: 0.9 }),
        () => w), { sides: 14, capStart: true, capEnd: true });
    } else {
      B.setSlot(KIT).setTint(0x4a4740);
      const bp = resample([[0, 1.668, -0.004], [0, 1.730, -0.004], [0, 1.792, -0.004]]
        .map((p) => [p[0], p[1] * S, p[2] * S]), 4);
      B.loft(tubeRings(bp, (d, t) => ({ rx: lerp(0.094, 0.030, t * t) * S, ry: lerp(0.102, 0.034, t * t) * S, round: 0.9 }),
        () => w), { sides: 12, capStart: false, capEnd: true });
    }
  }

  /* ---- limbs ---- */
  for (const side of ['L', 'R']) {
    const sh = rig.bind['upperArm' + side], el = rig.bind['forearm' + side], wr = rig.bind['hand' + side];
    B.setSlot(KIT).setTint(look.uniform);
    const ap = resample([
      [sh.x * 0.8, sh.y + 0.04 * S, sh.z], [sh.x, sh.y, sh.z],
      [el.x, el.y, el.z], [wr.x, wr.y, wr.z],
    ], 6);
    const A = 'upperArm' + side, F = 'forearm' + side, H = 'hand' + side;
    const eD = Math.hypot(sh.x * 0.2, 0.04 * S) + sh.distanceTo(el);
    B.loft(tubeRings(ap, (d, t) => ({
      rx: lerp(0.066, 0.034, t) * lerp(1, G, 0.6) * S, ry: lerp(0.068, 0.036, t) * lerp(1, G, 0.6) * S, round: 0.86,
    }), makeWeightFn(rig, [
      [0, { chest: 0.5, [A]: 0.5 }], [0.10 * S, { [A]: 1 }], [eD, { [A]: 0.5, [F]: 0.5 }],
      [eD + 0.10 * S, { [F]: 1 }], [eD + el.distanceTo(wr), { [F]: 0.4, [H]: 0.6 }],
    ])), { sides: 8, capStart: true, capEnd: false });

    // a mitten: no fingers, but it is where a hand is and it is glove-coloured
    B.setSlot(look.gloves ? KIT : SKIN).setTint(look.gloves ? look.glove : look.skin);
    const anchors = weaponAnchors(rig);
    const hp = side === 'R'
      ? new THREE.Vector3(wr.x + 0.014 * S, wr.y - 0.036 * S, wr.z - 0.014 * S)
      : anchors.weaponLeftGrip.clone().add(new THREE.Vector3(-0.016 * S, 0.024 * S, 0));
    B.superBox({
      centre: hp, half: [0.030 * S, 0.048 * S, 0.036 * S], round: 0.55, segU: 8, segV: 4,
      w: [[I('hand' + side), 1]],
    });

    const hip = rig.bind['thigh' + side], kn = rig.bind['shin' + side], an = rig.bind['foot' + side];
    B.setSlot(KIT).setTint(look.uniform);
    const lp = resample([
      [hip.x, hip.y + 0.07 * S, hip.z], [hip.x, hip.y, hip.z],
      [kn.x, kn.y, kn.z], [an.x, an.y + 0.02 * S, an.z],
    ], 7);
    const TH = 'thigh' + side, SN = 'shin' + side, FT = 'foot' + side;
    const kD = 0.07 * S + hip.distanceTo(kn);
    B.loft(tubeRings(lp, (d, t) => ({
      rx: lerp(0.100, 0.056, t) * lerp(1, G, 0.5) * S, ry: lerp(0.102, 0.060, t) * lerp(1, G, 0.5) * S, round: 0.82,
    }), makeWeightFn(rig, [
      [0, { hips: 0.7, [TH]: 0.3 }], [0.12 * S, { [TH]: 1 }], [kD, { [TH]: 0.5, [SN]: 0.5 }],
      [kD + 0.10 * S, { [SN]: 1 }], [kD + kn.distanceTo(an), { [SN]: 0.6, [FT]: 0.4 }],
    ])), { sides: 8, capStart: true, capEnd: false });

    B.setSlot(KIT).setTint(look.boot);
    B.superBox({
      centre: new THREE.Vector3(an.x, 0.062 * S, an.z + 0.036 * S),
      half: [0.052 * S, 0.062 * S, 0.130 * S], round: 0.42, segU: 8, segV: 4,
      w: [[I(FT), 0.7], [I('toe' + side), 0.3]],
    });
  }

  /* ---- the carbine, four boxes ---- */
  if (look.weapon) {
    const h = rig.bind.handR;
    const w = [[I('handR'), 1]];
    const P = (x, y, z) => new THREE.Vector3(h.x + x * S, h.y + y * S, h.z + z * S);
    B.setSlot(KIT).setTint(0x303236);
    const box = (c, half) => B.superBox({ centre: c, half: half.map((v) => v * S), round: 0.3, segU: 6, segV: 4, w });
    box(P(0, 0.050, 0.100), [0.024, 0.040, 0.190]);   // receiver + handguard
    box(P(0, 0.052, 0.380), [0.013, 0.013, 0.090]);   // barrel
    box(P(0, -0.048, -0.014), [0.019, 0.052, 0.028]); // grip
    box(P(0, 0.048, -0.150), [0.021, 0.040, 0.100]);  // stock
    box(P(0, -0.030, 0.086), [0.018, 0.074, 0.032]);  // magazine
  }

  return weaponAnchors(rig);
}
